import { useState, useRef, useEffect } from "react";
import {
  Upload,
  Download,
  Trash2,
  FileSpreadsheet,
  AlertCircle,
  Eye,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Alert, AlertDescription } from "../ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { useWorkPaperTemplateFile } from "../../hooks/useWorkPaperTemplateFile";
import ExcelViewer from "../../pages/extraction/viewExtraction/ExcelViewer";
import { LoadingSpinner } from "../ui/loadingSpinner/loadingSpinner";
import { cn } from "../../lib/utils";
import { validateFileSizeWithToast } from "../../lib/fileUploadValidation";

interface ExcelTemplateFileManagerProps {
  templateId: string | null;
  isReadOnly?: boolean;
}

export function ExcelTemplateFileManager({
  templateId,
  isReadOnly = false,
}: ExcelTemplateFileManagerProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    uploading,
    deleting,
    checking,
    downloading,
    fetchingUrl,
    fileStatus,
    error,
    clearError,
    checkFileStatus,
    uploadFile,
    getDownloadUrl,
    downloadFile,
    deleteFile,
  } = useWorkPaperTemplateFile({
    templateId,
  });

  useEffect(() => {
    if (templateId) {
      checkFileStatus();
    }
  }, [templateId, checkFileStatus]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!validateFileSizeWithToast(selectedFile)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (fileStatus?.hasFile) {
      setPendingFile(selectedFile);
      setReplaceDialogOpen(true);
    } else {
      handleUpload(selectedFile, false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async (file: File, replace: boolean) => {
    await uploadFile(file, replace);
    setPendingFile(null);
    setReplaceDialogOpen(false);
  };

  const handleDownload = async () => {
    await downloadFile();
  };

  const handleView = async () => {
    const url = await getDownloadUrl();
    if (url) {
      setViewerUrl(url);
      setViewDialogOpen(true);
    }
  };

  const handleCloseViewer = () => {
    setViewDialogOpen(false);
    setTimeout(() => setViewerUrl(null), 300);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseViewer();
    }
  };

  const handleDelete = async () => {
    const success = await deleteFile();
    if (success) {
      setDeleteDialogOpen(false);
    }
  };

  const hasFile = fileStatus?.hasFile && fileStatus?.fileExistsInS3;

  if (!templateId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Save the template first to manage Excel template files.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={clearError}
              className="ml-4 hover:opacity-70"
              aria-label="Dismiss error"
            >
              <span className="sr-only">Dismiss</span>×
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* File status and actions */}
      <div className="border rounded-lg p-4 space-y-4">
        {checking ? (
          <div className="flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : hasFile ? (
          <div className="space-y-4">
            {/* File exists */}
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  Excel template file uploaded
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleView}
                disabled={fetchingUrl}
              >
                <Eye className="h-4 w-4 mr-2" />
                {fetchingUrl ? "Loading..." : "View"}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
              >
                <Download className="h-4 w-4 mr-2" />
                {downloading ? "Downloading..." : "Download"}
              </Button>

              {!isReadOnly && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading..." : "Replace"}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={deleting}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {deleting ? "Deleting..." : "Delete"}
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* No file */}
            <div className="flex items-center gap-3 text-muted-foreground">
              <FileSpreadsheet className="h-5 w-5" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  No Excel template file uploaded
                </p>
                <p className="text-xs">
                  Upload an Excel file to use as a template
                </p>
              </div>
            </div>

            {/* Upload button */}
            {!isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? "Uploading..." : "Upload Excel File"}
              </Button>
            )}
          </div>
        )}

        {/* Hidden file input */}
        {!isReadOnly && (
          <Input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
        )}
      </div>

      {/* Read-only info */}
      {isReadOnly && hasFile && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This is a base template and cannot be modified. To make changes,
            please duplicate this template first.
          </AlertDescription>
        </Alert>
      )}

      {/* Replace confirmation dialog */}
      <AlertDialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing file?</AlertDialogTitle>
            <AlertDialogDescription>
              A template file already exists. Uploading a new file will replace
              the existing one. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setPendingFile(null)}
              disabled={uploading}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={() => pendingFile && handleUpload(pendingFile, true)}
              loading={uploading}
            >
              Replace File
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template file?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the Excel template file. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <Button
              onClick={handleDelete}
              loading={deleting}
              variant="destructive"
            >
              Delete File
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excel Viewer Sidebar */}
      {viewDialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50"
          onClick={handleBackdropClick}
        >
          <div
            className={cn(
              "fixed right-0 top-0 h-full w-[70vw] bg-white shadow-2xl",
              "transform transition-transform duration-300 ease-in-out",
              "flex flex-col",
              viewDialogOpen ? "translate-x-0" : "translate-x-full",
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Excel Template Preview</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseViewer}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>

            {/* Excel Viewer Content */}
            <div className="flex-1 overflow-hidden p-6">
              {viewerUrl && <ExcelViewer url={viewerUrl} visible={true} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
